import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, Star, Gift, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface CTABannerProps {
  variant?: "newsletter" | "firstOrder" | "loyalty" | "seasonal" | "freeShipping";
  className?: string;
}

interface CTASection {
  id: string;
  section_key: string;
  title: string;
  subtitle: string | null;
  button_text: string;
  is_active: boolean;
  background_gradient: string | null;
  icon_name: string | null;
}

export const CTABanner = ({ variant, className = "" }: CTABannerProps) => {
  const navigate = useNavigate();

  const { data: ctaSections } = useQuery({
    queryKey: ['active-cta-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cta_sections')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data as CTASection[];
    }
  });

  const activeSection = ctaSections?.find(section => 
    variant ? section.section_key === variant : true
  ) || ctaSections?.[0];

  if (!activeSection) return null;

  const getIcon = () => {
    switch (activeSection.icon_name) {
      case 'Heart': return <Heart className="h-5 w-5" />;
      case 'Gift': return <Gift className="h-5 w-5" />;
      case 'Star': return <Star className="h-5 w-5" />;
      case 'Mail': return <Mail className="h-5 w-5" />;
      default: return <ArrowRight className="h-5 w-5" />;
    }
  };

  const handleClick = () => {
    if (activeSection.section_key === 'newsletter') {
      navigate("/profile");
    } else {
      navigate("/category/all-products");
    }
  };

  return (
    <div className={`rounded-lg border-2 border-primary/20 bg-gradient-to-r ${activeSection.background_gradient || 'from-primary/5 to-primary/10'} p-6 text-center ${className}`}>
      <div className="flex flex-col items-center space-y-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-sm">
          {getIcon()}
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-gray-900">{activeSection.title}</h3>
          {activeSection.subtitle && (
            <p className="text-gray-600 max-w-md mx-auto">{activeSection.subtitle}</p>
          )}
        </div>
        <Button 
          onClick={handleClick}
          className="bg-primary hover:bg-primary/90 text-white px-6 py-2"
        >
          {activeSection.button_text}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};